import { css } from '@emotion/react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { PersonsV3 } from '@frontend/api-person';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { AdaptoActions, FloatingActionBar } from '@frontend/adapto-actions';
import { ActionsUI, Actions } from '@frontend/contact-actions';
import { useGetDisplayName } from '@frontend/get-display-name';
import { FirstWordBoldText } from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import { Photos } from '@frontend/photos';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useContactPanelShallowStore } from '@frontend/shared';
import { useGetActivePetsQuery } from '@frontend/vet-components';
import { theme } from '@frontend/theme';
import { ListRow, Text } from '@frontend/design-system';
import { checkIsNonAttendedEventStatus } from '../../../components/calendar-view/utils';
import { useScheduleActionsContainerMethod } from '../../../context/ScheduleActionsContainerContext';
import { useViewOnCalendarAction } from '../../../hooks/calendar/use-view-on-calendar-action';
import { useGetEntityArchiveStatus } from '../../../hooks/scheduler-v3/use-get-calendar-v3-queries-data';
import { useGetAppointmentDetails } from '../../../hooks/use-get-appointment-details';
import { useIsScheduleSmallModeWindow } from '../../../hooks/use-schedule-small-mode';
import { ExtendedAppointment } from '../../../types';
import { getTimeString } from '../../../utils/date-helpers';
import { AppointmentBadgeStatus } from './AppointmentBadgeStatus';

const TRACKING_ID = 'schedule-pulse:appointment-list:action-menu';

type PatientListRowProps = {
  appt?: ExtendedAppointment;
  optOutText?: boolean;
  locationId: string;
  shouldShowAppointmentModal?: boolean;
  appointments?: Appointment[];
  shouldShowAppointmentStatus?: boolean;
  shouldShowActionMenu?: boolean;
};

export const PatientListRow = ({
  optOutText,
  appt,
  locationId,
  shouldShowAppointmentModal = false,
  appointments,
  shouldShowAppointmentStatus = true,
  shouldShowActionMenu = false,
}: PatientListRowProps) => {
  const { t } = useTranslation('schedule-pulse');
  const { isSmallModeWindow } = useIsScheduleSmallModeWindow();

  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

  const { setPersonId } = useContactPanelShallowStore('setPersonId');

  const start = getTimeString(appt?.start);
  const duration = appt?.duration ? `(${appt.duration} Min)` : '';
  const timeDisplay = start ? `${start}${duration ? ` ${duration}` : ''}` : '-';

  const { isAppointmentTypeArchived, isProviderArchived } = useGetEntityArchiveStatus({
    appointmentTypeId: appt?.appointmentTypeId || '',
    providerId: appt?.providerIds?.[0] || '',
    locationId,
  });

  const { closeModal: closeTrayModal } = useScheduleActionsContainerMethod();
  const isTrayModalOpen = useMatchMedia({ maxWidth: breakpoints.large.max });
  const { actionProps: viewOnCalendarActionProps } = useViewOnCalendarAction({
    eventId: appt?.id,
    eventDate: appt?.start,
  });

  const personId = appt?.person?.personId || '';

  const { lastUsedVertical } = useLastUsedVerticalShallowStore('lastUsedVertical');
  const isVet = lastUsedVertical === 'VET';

  const serviceableId = appt?.serviceableIds?.[0];
  const petQuery = useGetActivePetsQuery({
    personId: isVet ? personId : '',
    locationId: isVet ? locationId : '',
  });
  const pet = petQuery.data?.find((p) => p.petServiceableId === serviceableId);

  const { data: personData } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      personId,
      locationIds: [locationId],
    },
    {
      enabled: !!personId,
      select: (data) => PersonsV3.PersonHelpers.convertPersonV3ToPerson(data),
    }
  );

  const { openModal: openAppointmentModal } = useGetAppointmentDetails({
    appointmentId: appt?.id || '',
    locationId: locationId || appt?.locationId || '',
    appointments,
  });

  const { handleCallActionClick } = ActionsUI.actions.usePhoneCallAction({
    context: {
      personId: appt?.person?.personId ?? '',
      phoneNumber: appt?.person?.mobilePhone ?? '',
      locationId: appt?.locationId,
    },
  });

  const handleOpenAppointmentModal = () => {
    if (shouldShowAppointmentModal) openAppointmentModal();
  };

  const handleSetPersonIdAndLocationId = (appointment?: Appointment) => {
    if (appointment?.person?.personId && appointment?.locationId) {
      const personId = appointment.person.personId;
      const locationId = appointment.locationId;
      setPersonId(personId, true, locationId);
    }
  };

  const shouldShowOptOutText = optOutText;

  const gridTemplateAreas = `
  "name name name name"
  "time time ${appt?.practitionerName ? 'practitioner practitioner' : 'time time'}"
  "type type ${appt?.operatory ? 'operatory operatory' : 'type type'}"
`;

  const isNonAttendedV3Event = isScheduleV3FlagEnabled
    ? checkIsNonAttendedEventStatus(appt?.statusOfficeView as EventStatus)
    : false;

  const { fullName, firstName, lastName, legalName } = useGetDisplayName({
    person: {
      firstName: appt?.person?.firstName ?? '',
      lastName: appt?.person?.lastName ?? '',
      preferredName: appt?.person?.preferredName,
    },
  });

  return (
    <FloatingActionBar disabled={!shouldShowActionMenu}>
      <FloatingActionBar.Actions>
        <>
          <Actions.Message
            context={{ personId: personData?.PersonID, locationId }}
            label={t('Send Message')}
            trackingIdSuffix={`${TRACKING_ID}:message`}
            onClick={() => {
              // Temporary fix: The message thread opens underneath Schedule Pulse when Schedule Pulse is a tray modal
              if (closeTrayModal && isTrayModalOpen) {
                closeTrayModal();
              }
            }}
          />
          <Actions.Call
            context={{ personId: personData?.PersonID, locationId }}
            label={t('Click to Call')}
            trackingIdSuffix={`${TRACKING_ID}:call`}
          />
          <Actions.Payments
            context={{ personId: personId, locationId }}
            label={t('Request Payment')}
            trackingIdSuffix={`${TRACKING_ID}:request-payment`}
          />
          {personData && (
            <Actions.SendForm
              label={t('Send Form')}
              context={{ person: personData, locationId }}
              trackingIdSuffix={`${TRACKING_ID}:send-form`}
            />
          )}
          {!isSmallModeWindow && (
            <AdaptoActions.Action trackingId={`${TRACKING_ID}:view-on-calendar`} {...viewOnCalendarActionProps} />
          )}
        </>
      </FloatingActionBar.Actions>
      <FloatingActionBar.Content>
        <ListRow css={{ border: 0, padding: theme.spacing(1, 2) }} onClick={handleOpenAppointmentModal}>
          <ListRow.Lead css={{ alignItems: 'start', paddingRight: theme.spacing(1) }}>
            <Photos.ContactProfilePhoto
              size='small'
              personId={(appt?.person && appt.person?.personId) ?? ''}
              firstName={firstName}
              lastName={lastName}
              title={legalName}
              onClick={() => handleSetPersonIdAndLocationId(appt)}
            />
          </ListRow.Lead>
          <ListRow.Content
            css={{
              display: 'grid',
              gridTemplateAreas: gridTemplateAreas,
              gridTemplateColumns: 'repeat(4, 1fr)',
              gridTemplateRows: 'repeat(3, auto)',
              gap: theme.spacing(0.5),
            }}
          >
            <FirstWordBoldText css={{ textAlign: 'left', gridArea: 'name' }}>
              {isVet && pet?.name ? `${pet.name} ${appt?.person?.lastName ?? ''}` : fullName || t('Unknown')}
            </FirstWordBoldText>
            <Text css={[textStyles(isNonAttendedV3Event), { gridArea: 'time' }]}>{timeDisplay}</Text>
            {appt?.practitionerName && (
              <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
                {isProviderArchived && <Icon name='archive-small' color='subdued' />}
                <Text css={[textStyles(isNonAttendedV3Event), { gridArea: 'practitioner' }]}>
                  {appt?.practitionerName}
                </Text>
              </div>
            )}
            <Text css={[textStyles(isNonAttendedV3Event), { gridArea: 'type' }]}>
              <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
                {isAppointmentTypeArchived && <Icon name='archive-small' color='subdued' />}
                {appt?.type ?? '-'}
              </div>
            </Text>
            {appt?.operatory && (
              <Text css={[textStyles(isNonAttendedV3Event), { gridArea: 'operatory' }]}>{appt?.operatory}</Text>
            )}
          </ListRow.Content>
          <ListRow.Trail
            css={{
              justifySelf: 'end',
              gap: theme.spacing(1),
              paddingLeft: theme.spacing(0.5),
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            {shouldShowOptOutText && (
              <AdaptoActions size='small' iconColor='subdued' variant='action-buttons'>
                <AdaptoActions.Action
                  icon='phone'
                  hoverLabel='Click to Call'
                  label={t('Click to Call')}
                  color='icon'
                  onClick={handleCallActionClick}
                />
              </AdaptoActions>
            )}
            {shouldShowAppointmentStatus && (
              <AppointmentBadgeStatus status={appt?.statusOfficeView ?? ''} appointmentId={appt?.id ?? ''} iconOnly />
            )}
          </ListRow.Trail>
        </ListRow>
      </FloatingActionBar.Content>
    </FloatingActionBar>
  );
};

const textStyles = (isNonAttendedV3Event: boolean) => css`
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${theme.colors.neutral70};
  font-size: ${theme.fontSize(12)};
  text-decoration: ${isNonAttendedV3Event ? 'line-through' : 'none'};
`;
